// AutoFlattenPage — Live auto-flatten status & timer
// Shows time until EOD flatten, current open positions, what gets flattened, manual override

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { TfCard, TfCardFloating, TfSectionHeaderView, TfStatRow } from '../../components/TfPrimitives';

interface OpenPos {
  id: string;
  symbol: string;
  side: 'long' | 'short';
  qty: number;
  entry: number;
  current: number;
  pnl: number;
  flattenIn: number; // seconds
}

const MOCK_POS: OpenPos[] = [
  { id: 'p1', symbol: 'ES', side: 'long',  qty: 2,  entry: 5430.25, current: 5448.50, pnl: 365,   flattenIn: 5400 },
  { id: 'p2', symbol: 'NQ', side: 'short', qty: 1,  entry: 19250.00, current: 19205.00, pnl: 225, flattenIn: 5400 },
  { id: 'p3', symbol: 'CL', side: 'long',  qty: 1,  entry: 78.40, current: 78.85,    pnl: 450,  flattenIn: 5400 },
];

export function AutoFlattenPage() {
  const nav = useNavigate();
  const [now, setNow] = useState(new Date());
  const [positions, setPositions] = useState(MOCK_POS);
  const [overrideOn, setOverrideOn] = useState(false);

  useEffect(() => {
    const t = setInterval(() => {
      setNow(new Date());
      setPositions((curr) => curr.map((p) => ({ ...p, flattenIn: Math.max(0, p.flattenIn - 1) })));
    }, 1000);
    return () => clearInterval(t);
  }, []);

  const eod = new Date(now);
  eod.setHours(16, 0, 0, 0);
  if (eod < now) eod.setDate(eod.getDate() + 1);
  const ms = eod.getTime() - now.getTime();
  const hours = Math.floor(ms / 3600000);
  const mins = Math.floor((ms % 3600000) / 60000);
  const secs = Math.floor((ms % 60000) / 1000);
  const totalSecs = hours * 3600 + mins * 60 + secs;
  const totalPnl = positions.reduce((s, p) => s + p.pnl, 0);

  return (
    <div className="min-h-screen bg-tf-surface-1 pb-8">
      <div className="sticky top-0 z-10 flex h-11 items-center justify-between border-b border-tf-border ios26-navbar px-2 backdrop-blur-xl">
        <button onClick={() => nav(-1)} data-testid="back" className="flex h-9 w-9 items-center justify-center text-tf-blue haptic">
          <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2.4"><path d="M15 5l-7 7 7 7" strokeLinecap="round" strokeLinejoin="round" /></svg>
        </button>
        <div className="absolute left-1/2 -translate-x-1/2 text-[15px] font-semibold">Auto-flatten</div>
        <div className="w-9" />
      </div>

      {/* Hero — countdown timer + pill buttons */}
      <div className="mx-4 mt-4">
        <TfCardFloating className="text-center">
          <div className="text-[13px]" style={{ color: '#8E8E93' }}>{totalSecs < 600 ? '⚠️ Less than 10 min' : '⏰ Auto-flatten in'}</div>
          <div className="mt-1 flex items-baseline justify-center gap-1">
            <span className="text-[40px] font-bold tabular-nums leading-none" style={{ color: totalSecs < 600 ? '#FF3B30' : '#1C1C1E' }}>
              {String(hours).padStart(2, '0')}:{String(mins).padStart(2, '0')}:{String(secs).padStart(2, '0')}
            </span>
          </div>
          <div className="mt-1 flex items-center justify-center gap-1 text-[13px]">
            <span className="text-tf-text-3">Positions close at 16:00 CT · No rollover</span>
          </div>
          <div className="mt-4 grid grid-cols-2 gap-3">
            <button onClick={() => setOverrideOn(true)} className="flex h-12 items-center justify-center gap-1.5 rounded-full bg-tf-text-1 text-[15px] font-semibold text-white active:scale-[0.97] transition-transform">
              Close all
              <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M7 17L17 7" /><path d="M7 7h10v10" />
              </svg>
            </button>
            <button onClick={() => alert('Holding through EOD')} className="flex h-12 items-center justify-center gap-1.5 rounded-full bg-tf-text-1 text-[15px] font-semibold text-white active:scale-[0.97] transition-transform">
              Hold
              <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M17 7L7 17" /><path d="M17 17H7V7" />
              </svg>
            </button>
          </div>
        </TfCardFloating>
      </div>

      {/* 3-up stat row */}
      <TfStatRow
        className="mt-4"
        stats={[
          { label: 'Open positions', value: `${positions.length}`, color: '#1C1C1E' },
          { label: 'Today P&L', value: `+$${totalPnl.toLocaleString()}`, color: totalPnl >= 0 ? '#34C759' : '#FF3B30' },
          { label: 'EOD closes at', value: '16:00', color: '#FF9500' },
        ]}
      />

      {/* Manual override */}
      <div className="mx-4 mt-3">
        <TfCard className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-[14px] font-bold text-tf-text-1">Manual close all</h2>
              <p className="text-[11px] text-tf-text-3 mt-0.5">Closes all positions now at market price</p>
            </div>
            <button
              onClick={() => setOverrideOn(!overrideOn)}
              data-testid="manual-close-toggle"
              className={['h-7 w-12 rounded-full transition', overrideOn ? 'bg-tf-loss' : 'bg-tf-border'].join(' ')}
            >
              <div className={['h-5 w-5 rounded-full bg-white mt-1 transition', overrideOn ? 'ml-6' : 'ml-1'].join(' ')} />
            </button>
          </div>
          {overrideOn && (
            <div className="mt-3 rounded-[10px] border border-tf-loss bg-tf-red-tint p-3">
              <p className="text-[12px] text-tf-loss">⚠️ Closing all positions now. This will trigger a "user closed" event in your trade log.</p>
              <button onClick={() => alert('All positions closed at market.')} data-testid="confirm-manual-close" className="mt-2 w-full h-10 rounded-full bg-tf-loss text-white text-[13px] font-bold active:opacity-40">Close all positions now</button>
            </div>
          )}
        </TfCard>
      </div>

      {/* Open positions */}
      <TfSectionHeaderView title={`Open positions · ${positions.length}`} viewAllLabel="View all" onViewAll={() => {}} />
      <div className="mx-4">
        <TfCard>
          {positions.map((p, i) => {
            const positive = p.pnl >= 0;
            const secs = p.flattenIn;
            const hm = Math.floor(secs / 3600);
            const mm = Math.floor((secs % 3600) / 60);
            return (
              <div key={p.id} data-testid={`pos-${p.id}`} className={['flex items-center gap-3 px-4 py-3', i < positions.length - 1 ? 'border-b border-tf-border' : ''].join(' ')}>
                <div className={['flex h-10 w-10 items-center justify-center rounded-[10px] text-[12px] font-black',
                  p.side === 'long' ? 'bg-tf-green-tint text-tf-profit' : 'bg-tf-red-tint text-tf-loss'
                ].join(' ')}>
                  {p.side === 'long' ? '↑' : '↓'}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[14px] font-bold text-tf-text-1">{p.symbol} <span className="text-[11px] font-normal text-tf-text-3">{p.qty} {p.qty === 1 ? 'lot' : 'lots'}</span></div>
                  <div className="text-[11px] text-tf-text-3 tabular-nums">Entry {p.entry} → {p.current}</div>
                  <div className="mt-0.5 text-[10px] text-tf-text-3">Flattens in <span className="font-semibold text-tf-text-1 tabular-nums">{String(hm).padStart(2, '0')}:{String(mm).padStart(2, '0')}</span></div>
                </div>
                <div className="text-right">
                  <div className={['text-[15px] font-bold tabular-nums', positive ? 'text-tf-profit' : 'text-tf-loss'].join(' ')}>{positive ? '+' : ''}${p.pnl.toLocaleString()}</div>
                  <div className="text-[10px] text-tf-text-3">@ market</div>
                </div>
              </div>
            );
          })}
        </TfCard>
      </div>

      {/* How it works */}
      <TfSectionHeaderView title="How auto-flatten works" />
      <div className="mx-4">
        <TfCard>
          {[
            { t: '15:55 CT', label: 'Warning',         sub: 'Email heads-up if positions are still open' },
            { t: '16:00 CT', label: 'Flatten all',     sub: 'Market orders sent for every open position' },
            { t: '16:01 CT', label: 'Confirm fills',   sub: 'Trades logged with timestamp + price' },
            { t: '16:05 CT', label: 'Account freeze',  sub: 'No trading until next day opens' },
          ].map((s, i, a) => (
            <div key={s.label} data-testid={`step-${i}`} className={['flex items-start gap-3 px-4 py-3', i < a.length - 1 ? 'border-b border-tf-border' : ''].join(' ')}>
              <div className="w-16 text-[11px] font-bold text-tf-text-3 tabular-nums mt-0.5">{s.t}</div>
              <div>
                <div className="text-[14px] font-semibold text-tf-text-1">{s.label}</div>
                <div className="text-[11px] text-tf-text-3">{s.sub}</div>
              </div>
            </div>
          ))}
        </TfCard>
      </div>
    </div>
  );
}
